// getLivestreamComputedData.js
'use strict';
import createCachedSelector from '../resource/createCachedSelector.js';

import getLiveStreamData, {
  getIsMeExclusivePrivateBuyer,
} from '../selector/getLiveStreamData.js';
import getOperationData from '../selector/getOperationData.js';
import getPrivateShowStatus from '../selector/getPrivateShowStatus.js';

import {
  PRESET_SD,
  PRESET_PREVIEW,
  LivestreamMode,
  ExclusivePrivateStatus,
  privateShowStatus as privateShowStatusConstant,
} from '../resource/liveStreamConstants.js';

/**
 * Select whether the livestream has a valid stream token.
 * @kind selector
 * @param {Immutable.Map} state - root state.
 * @param {string} streamId - livestream id.
 * @return {boolean} True if the livestream has either byteplusToken or streamToken.
 */
const getHasStreamToken = createCachedSelector(
  (state, streamId) =>
    getOperationData(state, ['liveStream', 'paid', streamId], 'byteplusToken'),
  (state, streamId) =>
    getOperationData(state, ['liveStream', 'paid', streamId], 'streamToken'),
  (streamToken, byteplusToken) => !!(streamToken || byteplusToken)
)((state, streamId) => `${streamId}`);

/**
 * Select livestream overlay mode by stream id.
 * @kind selector
 * @param {Immutable.Map} state - root state.
 * @param {string} streamId - stream id.
 * @return {string} Livestream mode.
 */
const getLivestreamMode = createCachedSelector(
  (state, streamId) =>
    getLiveStreamData(state, [streamId, 'exclusivePrivate'], 'status'),
  (state, streamId) => getPrivateShowStatus(state, streamId),
  (state, streamId) => getLiveStreamData(state, [streamId], 'status'),
  (exclusivePrivateStatus, privateShowStatus, streamStatus) => {
    if (streamStatus === PRESET_PREVIEW) {
      return LivestreamMode.FREE;
    }
    if (
      [
        ExclusivePrivateStatus.PREPARING,
        ExclusivePrivateStatus.PERFORMING,
      ].includes(exclusivePrivateStatus)
    ) {
      return LivestreamMode.ONE_VS_ONE;
    }

    if (
      [
        privateShowStatusConstant.PREPARING,
        privateShowStatusConstant.PERFORMING,
      ].includes(privateShowStatus)
    ) {
      return LivestreamMode.SHOW;
    }

    if (streamStatus === PRESET_SD) {
      return LivestreamMode.ONE_VS_N;
    }

    return LivestreamMode.FREE;
  }
)((state, streamId) => `${streamId}`);

/**
 * Get should use overlay cta by stream id.
 * @kind selector
 * @param {Immutable.Map} state - root state.
 * @param {string} streamId - stream id.
 * @return {boolean} Return true if should use overlay cta.
 */
const getShouldUseOverlayCta = createCachedSelector(
  (state, streamId) => getLivestreamMode(state, streamId),
  (state, streamId) => getHasStreamToken(state, streamId),
  (state, streamId) => getIsMeExclusivePrivateBuyer(state, streamId),
  (modeName, hasStreamToken, isInitiator) => {
    const isMode1vsN = LivestreamMode.ONE_VS_N === modeName;
    const isModeShow = LivestreamMode.SHOW === modeName;
    const isMode1vs1 = LivestreamMode.ONE_VS_ONE === modeName;

    if (isMode1vsN && !hasStreamToken) return true;

    if (isModeShow && !hasStreamToken) return true;

    if (isMode1vs1 && isInitiator && !hasStreamToken) return true;

    return false;
  }
)((state, streamId) => `${streamId}`);

/**
 * Select whether the livestream has any toy device online.
 * @kind selector
 * @param {Immutable.Map} state - root state.
 * @param {string} streamId - livestream id.
 * @return {boolean} True if there is at least one device online.
 */
const getIsToyDeviceOnline = createCachedSelector(
  (state, streamId) => getLiveStreamData(state, [streamId], 'device'),
  devices => !!devices && Object.keys(devices).length > 0
)((state, streamId) => `${streamId}`);

export {
  getHasStreamToken,
  getLivestreamMode,
  getShouldUseOverlayCta,
  getIsToyDeviceOnline,
};
